import { zodResolver } from '@hookform/resolvers/zod';
import { Stack } from '@mantine/core';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { PaperControl } from '@/module/Author/Paper/PaperControl';
import { ArticleMembershipSelectControl } from '@/module/Nomen/ArticleMembership/ArticleMembershipSelectControl';
import { ArticleTypeSelectControl } from '@/module/Nomen/ArticleType/ArticleTypeSelectControl';

import { AuthorListControl, Content, Form, FormSubmit } from '@/core/components';

import { ArticleSchema } from './model/ArticleSchema';

interface ArticleFormProps extends FormSubmit<ArticleSchema> {
  title: string;
  showWosFields: boolean;
  showScopusFields: boolean;
}

export function ArticleForm(props: ArticleFormProps) {
  const { title, schema, defaultValues, values, isSubmitting, onCancel, onSubmit, showWosFields, showScopusFields } =
    props;
  const { t } = useTranslation();
  const form = useForm<ArticleSchema>({
    defaultValues,
    values,
    resolver: zodResolver(schema),
  });

  return (
    <FormProvider {...form}>
      <Form
        title={title}
        isSubmitting={isSubmitting}
        onCancel={onCancel}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <Stack>
          <Content type="full">
            {showWosFields && (
              <ArticleTypeSelectControl<ArticleSchema>
                label={t('articles.form.fields.articleType')}
                name="type"
                isSubmitting={isSubmitting}
              />
            )}
            <PaperControl
              isSubmitting={isSubmitting}
              showScopusFields={showScopusFields}
              showWosFields={showWosFields}
            />
          </Content>
          <Content type="full">
            <AuthorListControl<ArticleSchema>
              name="authors"
              isSubmitting={isSubmitting}
              label={t('authors.form.title')}
              renderMembership={({ membershipName }) => (
                <ArticleMembershipSelectControl
                  label={t('authors.form.fields.membership')}
                  name={membershipName}
                  isSubmitting={isSubmitting}
                  isDisabled={isSubmitting}
                />
              )}
            />
          </Content>
        </Stack>
      </Form>
    </FormProvider>
  );
}
